import type {
  BookingGuest,
  ChatMessageIntent,
  ChatReply,
  ChatTurnResponse,
  SessionResumeResponse,
  WidgetSessionResponse,
  WidgetEventsResponse,
} from '../shared/contracts';
import {
  getWidgetCopy,
  getWidgetErrorMessage,
} from './i18n';

export class WidgetApiError extends Error {
  constructor(
    message: string,
    readonly code = 'REQUEST_FAILED',
  ) {
    super(message);
    this.name = 'WidgetApiError';
  }
}

export interface MessageOptions {
  messageId?: string;
  intent?: ChatMessageIntent;
  slotToken?: string;
  serviceId?: number;
  date?: string;
}

export class WidgetApi {
  constructor(
    private readonly baseUrl: string,
    private readonly locale = 'en',
    private readonly timeoutMs = 40_000,
  ) {}

  private async request<T>(endpoint: string, init?: RequestInit): Promise<T> {
    const copy = getWidgetCopy(this.locale);
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...init,
        signal: init?.signal || AbortSignal.timeout(this.timeoutMs),
        headers: {
          Accept: 'application/json',
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...init?.headers,
        },
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        throw new WidgetApiError(
          copy.requestTimeout,
          'REQUEST_TIMEOUT',
        );
      }
      throw new WidgetApiError(
        copy.networkError,
        'NETWORK_ERROR',
      );
    }
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const code =
        typeof data?.error?.code === 'string'
          ? data.error.code
          : 'REQUEST_FAILED';
      throw new WidgetApiError(
        getWidgetErrorMessage(
          this.locale,
          code,
          typeof data?.error?.message === 'string'
            ? data.error.message
            : undefined,
        ),
        code,
      );
    }
    if (data === null) {
      throw new WidgetApiError(copy.invalidResponse);
    }
    return data as T;
  }

  async startSession(
    salonSlug: string,
    locale: string,
    market = 'cz',
  ): Promise<WidgetSessionResponse> {
    const session = await this.request<WidgetSessionResponse>('/api/chat/sessions', {
      method: 'POST',
      body: JSON.stringify({ salonSlug, locale, market }),
    });
    return this.assertMarket(session, market);
  }

  async resumeSession(
    sessionId: string,
    visitorToken: string,
    market = 'cz',
  ): Promise<SessionResumeResponse> {
    const session = await this.request<SessionResumeResponse>(
      `/api/chat/sessions/${encodeURIComponent(sessionId)}`,
      { headers: this.visitorHeaders(visitorToken) },
    );
    return this.assertMarket(session, market);
  }

  getEvents(
    sessionId: string,
    visitorToken: string,
    after: number,
  ): Promise<WidgetEventsResponse> {
    return this.request(
      `/api/chat/sessions/${encodeURIComponent(sessionId)}/events?after=${after}`,
      { headers: this.visitorHeaders(visitorToken) },
    );
  }

  sendMessage(
    sessionId: string,
    visitorToken: string,
    message: string,
    options?: MessageOptions,
  ): Promise<ChatTurnResponse> {
    return this.request(`/api/chat/sessions/${encodeURIComponent(sessionId)}/messages`, {
      method: 'POST',
      headers: this.visitorHeaders(visitorToken),
      body: JSON.stringify({ message, ...options }),
    });
  }

  createBooking(
    sessionId: string,
    visitorToken: string,
    draftToken: string,
    guest: BookingGuest,
  ): Promise<ChatReply> {
    return this.request(`/api/chat/sessions/${encodeURIComponent(sessionId)}/bookings`, {
      method: 'POST',
      headers: this.visitorHeaders(visitorToken),
      body: JSON.stringify({ draftToken, guest }),
    });
  }

  private visitorHeaders(visitorToken: string): HeadersInit {
    return { Authorization: `Bearer ${visitorToken}` };
  }

  private assertMarket<T extends { market: string }>(
    response: T,
    market: string,
  ): T {
    if (response.market !== market) {
      throw new WidgetApiError(
        getWidgetCopy(this.locale).invalidResponse,
        'MARKET_MISMATCH',
      );
    }
    return response;
  }
}
